

import React, { Component } from 'react';
import cx from 'classnames';
import isEqual from 'lodash.isequal';
import isEmpty from 'lodash.isempty';
import _get from 'lodash.get';
import globalConfig from 'react-global-configuration';
import { credentials } from '@shootsta/client-auth';
import { PROJECT_TYPE } from '@shootsta/common-domain-data';

import { Pencil, AlertTriangle } from 'lucide-react';
import {
  getOutputSummaryWithAdditionalOutputs,
  getOutputSummary
} from '@shootsta/addons-summarizer';
import { getCostValue } from '@shootsta/cost-parsing-utils';
import { FormModal, Input, Text, Link } from '../../../ui-kit';
import AddonsPayment from './AddonsPayment';
import ChangePaymentMethodModal from './ChangePaymentMethodModal';
import ChangesModal from '../../ChangesModal';
import { notify } from '../../Notifications';
import { detachMaster, showBufferCreditWarning, isShootstaRole, getFlagStatus } from '../../../common/utils';
import { getChangesModalConstants } from '../utils';
import contractBalanceForSubscription from '../utils/contractBalanceForSubscription';
import { withFlagsConsumer } from '../../FeatureFlags';
import LiveChatModal from '../../Credits/components/CreditsInfo/components/CreditMenuSubscription/components/LiveChatModal';
import resolveCanSubmit from '../../Credits/selectors/resolveCanSubmit';
import applyFinanceTeamCredits from '../../Credits/selectors/applyFinanceTeamCredits';
import buildBillablesFromLineItemsUtil from '../utils/buildBillablesFromLineItems';

import '../styles/_payments-invoice.scss';

const toCurrencyDecimal = (n) =>
  Number(n || 0).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

const OUTPUT_STATUS = {
  INTERNAL_REVIEW: 'INTERNAL_REVIEW',
  PENDING_REVIEW: 'PENDING_REVIEW',
  COST_REVIEW: 'COST_REVIEW'
};




















































const FAIR_PLAY_POLICY_LINK = 'https://shootsta.com/legal-stuff/';

function openFairPlayPolicy() {
  window.open(FAIR_PLAY_POLICY_LINK, '_blank');
}

const getSelectedTeamIdForSubscriptionsV2 = ({ creditType, teamDetails }) => {
  if (creditType === 'TEAM') {
    return teamDetails[0];
  }

  return '';
};

const getUserTeamCreditsByRole = ({
  organisationMemberships,
  teamCredits,
  userTeamIds,
  isWorkspaceAdmin
}) => {
  const {
    user: { email }
  } = { user: {}, ...credentials.get() };

  const currentMembership = organisationMemberships.find(
    (membership) => membership.userEmail === email
  );

  if (isEmpty(teamCredits)) return [];

  // Check if the user has a role that grants access to all teams
  if (isShootstaRole(currentMembership) || isWorkspaceAdmin) {
    // Return all teams if the user has broad access permissions
    return teamCredits;
  }

  // For users, return only the teams they are a part of
  return teamCredits.filter(({ team }) => userTeamIds.includes(team.teamId));
};

const canShowTeamCreditsInfo = ({ creditType }) => creditType === 'TEAM';

class PaymentsInvoice extends Component


{
  constructor(props) {
    super(props);

    this.state = {
      loading: false,
      showConfirmationModal: false,
      stayOpen: props.stayOpen,
      totalCost: -1,
      purchaseOrderId: '',
      orgCreditInfo: {},
      teamCreditInfo: [],
      selectedTeamId: '',
      showLiveChatModal: false,
      hasShutterStockAccess: false,
      showProceedToPayment: false,
      clientType: '',
      useShootsDollars: false,
      dollarsRemaining: 0,
      financeBusy: false,
      showChangePaymentMethodModal: false
    };
  }

  componentDidMount() {
    const { showCreditInfo, flags, financeBalance } = this.props;
    this.setTotalCost();
    this.setShutterStockAccess();

    if (showCreditInfo) {
      this.setCreditInfo();
    }

    // The balance is owned by CreditsProvider and arrives via context. If it
    // is already loaded by the time the modal mounts, seed the dollars
    // defaults now; otherwise componentDidUpdate handles the first arrival.
    if (flags?.financeLedgerEnabled && financeBalance) {
      this.applyFinanceDefaults(financeBalance);
    }
  }

  componentDidUpdate(prevProps) {
    const {
      videoOutputs: prevOutputs,
      financeBalance: prevBalance,
      showCreditInfo: prevShowCreditInfo
    } = prevProps;
    const {
      videoOutputs: newOutputs,
      flags,
      financeBalance,
      showCreditInfo
    } = this.props;

    if (!isEqual(prevOutputs, newOutputs)) {
      this.setTotalCost();
    }

    // setCreditInfo loads the credit/team context, but componentDidMount only
    // calls it when showCreditInfo is already true at mount. This modal can
    // mount before the workflow store's master output reads as DRAFT (e.g.
    // re-submitting an already-submitted project), leaving showCreditInfo false
    // at mount with no recovery — the team picker never loads and a team-mode
    // credit bill goes out with no teamAllocations (a guaranteed "teamAllocations
    // required in team mode" 400). Re-run the loader when showCreditInfo flips
    // true so the team context loads on this surface too.
    if (showCreditInfo && !prevShowCreditInfo) {
      this.setCreditInfo();
    }

    // Seed the dollars defaults once, on the first balance arrival
    // (null -> present). Guarding on the transition means the provider's 60s
    // poll won't overwrite a choice the user has since toggled.
    if (flags?.financeLedgerEnabled && !prevBalance && financeBalance) {
      this.applyFinanceDefaults(financeBalance);
    }
  }

  // Pre-select Shootsta Dollars when there's a positive balance — matches
  // Figma where the picker defaults to dollars.
  applyFinanceDefaults = (financeBalance) => {
    const dollarsRemaining = Number(financeBalance.dollarsRemaining) || 0;
    this.setState({
      dollarsRemaining,
      useShootsDollars: dollarsRemaining > 0
    });
  };

  setPurchaseOrderId = (evt) => {
    const { value } = evt.target;

    this.setState({ purchaseOrderId: value });
  };

  goToConfirmationModal = () => {
    this.setState({
      showConfirmationModal: true,
      stayOpen: false
    });
  };

  handleSave = async (selectedReasons = []) => {
    const { purchaseOrderId, totalCost, selectedTeamId } = this.state;
    const {
      onConfirm,
      onConfirmShootsDollars,
      onCreditsUpdated,
      flags,
      billFinanceWorkspace,
      video
    } = this.props;

    // The finance-ledger path bills the workspace and then performs its
    // platform action via `onConfirmShootsDollars`. Only enter it when that
    // callback is actually wired (the customer submit/payment flows). The
    // reject / request-changes modal (and elevate) reuse this component with
    // `onConfirm` only and no payment intent — for them, entering this branch
    // would fire billFinanceWorkspace AND silently drop the platform action
    // (handleSaveWithFinanceLedger never calls onConfirm), so the reject
    // mutation never runs. Gate on `onConfirmShootsDollars` so those flows
    // fall through to `onConfirm` below and never bill.
    if (
      flags?.financeLedgerEnabled &&
      billFinanceWorkspace &&
      video?.videoId &&
      onConfirmShootsDollars
    ) {
      await this.handleSaveWithFinanceLedger({ selectedReasons });
      onCreditsUpdated?.();
      return;
    }

    await onConfirm(
      {
        purchaseOrderId,
        clientRejectionReasons: selectedReasons,
        hasCost: Boolean(totalCost),
        ...(selectedTeamId ? { teamId: selectedTeamId } : {})
      }
    );

    onCreditsUpdated();
  };

  openChangePaymentMethodModal = () => {
    this.setState({ showChangePaymentMethodModal: true });
  };

  closeChangePaymentMethodModal = () => {
    this.setState({ showChangePaymentMethodModal: false });
  };

  handleChangePaymentMethod = (method) => {
    this.setState({ useShootsDollars: method === 'dollars' });
  };

  getBillFinanceErrorMessage = (error) => {
    const status =
      error?.networkError?.statusCode ??
      error?.statusCode ??
      error?.graphQLErrors?.[0]?.extensions?.statusCode;
    const message =
      error?.message ||
      error?.graphQLErrors?.[0]?.message ||
      error?.networkError?.result?.message ||
      '';
    const code =
      error?.graphQLErrors?.[0]?.extensions?.code ||
      error?.networkError?.result?.code;

    if (status === 409 || code === 'CONFLICT') {
      return 'Another user is editing this project — please refresh and try again';
    }
    if (
      status === 400 &&
      (code === 'INSUFFICIENT_BALANCE' || /insufficient/i.test(message))
    ) {
      return 'Insufficient Shootsta Dollars balance';
    }
    return 'Failed to bill workspace in Shootsta Dollars';
  };

  // Map an appliedAddon type to a finance-side billable.type so the
  // statement page renders the right category for itemised add-ons.
  // Mirrors the CostsTab admin flow's mapping for consistency.
  mapAddonToBillableType = (addonTypeKey) => {
    switch (addonTypeKey) {
      case 'BURN_IN_CAPTIONS':
        return 'addon:burn-in-captions';
      case 'SRT_CAPTIONS':
        return 'addon:srt-captions';
      case 'VOICE_OVER_ARTISTS':
      case 'VOICE_OVER_ARTIST':
        return 'addon:voice-over';
      case 'SUPERSIZE_MY_EDIT':
      case 'SUPERSIZE':
        return 'addon:supersize';
      default:
        return `addon:${String(addonTypeKey).toLowerCase().replace(/_/g, '-')}`;
    }
  };

  describeAddon = (addonTypeKey, addon) => (
    addon?.description
      || addon?.label
      || (addon?.language && addon.language.label)
      || addon?.name
      || addon?.value
      || addonTypeKey
  );

  // Walk the output's appliedAddons into per-addon line items, plus
  // a leading "deliverable" line item for any cost beyond the addons total.
  //
  // Uses `getOutputSummary` (which delegates to `getOutputAddonsReport`)
  // rather than `getOutputVersionSummary`: the latter requires
  // `output.versions[i].versionNumber === output.latestVersion` to match, and
  // in this customer-flow's reshape pipeline that match doesn't hold at
  // billing time — `outputsWithLatestVersions` in Footer.tsx replaces
  // `output.versions` with `[currentVersion]` but `output.latestVersion`
  // isn't always aligned. The strict-match path then returned null and the
  // itemiser fell back to a single deliverable lump (observed on CYX7E9,
  // RNH3ZR, etc.).
  //
  // `getOutputSummary` is the same path the display side
  // (`getOutputSummaryWithAdditionalOutputs.setTotalCost`) already uses,
  // so the modal's visible cost and the billed line items can't drift.
  // Look up the selected team's display name from the credit-info state
  // populated by setCreditInfo(). Falls back to the teamId when the
  // membership row hasn't loaded — finance still validates the teamId
  // against the workspace's teams, so the wrong display label can't
  // corrupt the ledger; the worst case is a teamId showing where a
  // team name would normally appear.
  getSelectedTeamName = () => {
    const { selectedTeamId, teamCreditInfo } = this.state;
    if (!selectedTeamId) return null;
    const entry = (teamCreditInfo || []).find(
      (t) => (t?.team?.teamId || t?.teamId) === selectedTeamId
    );
    return (
      entry?.team?.name ||
      entry?.team?.teamName ||
      entry?.name ||
      selectedTeamId
    );
  };

  // Credit cost rule. Mirrors hub3api-video/src/services/output/
  // submitMasterOutput.js (search `creditAmount`):
  //   ANIMATION or WEBINAR → 2 credits
  //   everything else (VIDEO, PODCAST)   → 1 credit
  //   non-master outputs (iterations, additional outputs) → 0 credits
  //
  // Lives inline rather than as a backend lookup. Pre-ADR-0006 this lived
  // in the platform's `recordCreditBillable` post-commit step, which
  // produced an addons-only interim state on /api/billWorkspace that
  // #110/#111 surfaced as stuck-with-addon-as-primary records. The FE
  // now writes the credit billable in the same snapshot as the addons.
  computeOutputCreditAmount = (output) => {
    if (!output?.isMaster) return 0;
    const { video } = this.props;
    return [PROJECT_TYPE.ANIMATION, PROJECT_TYPE.WEBINAR]
      .includes(video?.projectType)
      ? 2
      : 1;
  };

  // Walks the output's appliedAddons into per-addon line items and emits
  // a leading deliverable line. `isDeliverable: true` flags the row that
  // must carry isPrimary in the resulting billables — anchored to the
  // credit row (master) or the first dollar/invoice row (iteration /
  // additional output). Required by shootsta-finance ADR-0004 (server
  // rejects addon-typed primaries), ADR-0005 (iteration deliverable type
  // is `iteration`, never `addon:social-iteration`) and ADR-0006 (FE
  // writes the full snapshot per output in one call).
  buildLineItemsForOutput = (output, totalCost) => {
    const { video, addonsSelector } = this.props;
    const projectName = video?.name || `Video ${video?.videoId || ''}`.trim();
    const outputName = output?.name;
    const baseDesc = output?.isMaster
      ? outputName || projectName
      : outputName
        ? `${projectName} — ${outputName}`
        : projectName;

    const summary = getOutputSummary(output, addonsSelector || 'appliedAddons');
    const addonsList = (summary && summary.addonsList) || [];

    let addonsTotal = 0;
    const addonItems = [];
    addonsList.forEach((entry) => {
      if (!entry || typeof entry !== 'object') return;
      // Skip the GraphQL `__typename` key when choosing the addon key, and bail
      // on a metadata-only entry. Its value is a string, not an addon object, so
      // getCostValue parses it to 0 — which, now that $0 addons are kept, would
      // otherwise leak in as a bogus "__typename" line item.
      const addonTypeKey = Object.keys(entry).find((k) => k !== '__typename');
      if (!addonTypeKey) return;
      const addon = entry[addonTypeKey];
      if (!addon || typeof addon !== 'object') return;
      // Keep free ($0) addons (real deliverables, e.g. included EN SRT captions)
      // so they show on the finance statement. A genuine free addon still
      // carries its `cost` field as a zero string (e.g. "AUD $0", from getCost
      // for package-included addons); an entry with no `cost` field is not a
      // priced deliverable. getCostValue() returns 0 for both, so gate on the
      // raw field's presence — keep when present, skip when absent.
      if (!addon.cost) return;
      const cost = getCostValue(addon);
      if (cost < 0) return;
      addonsTotal += cost;
      addonItems.push({
        type: this.mapAddonToBillableType(addonTypeKey),
        desc: this.describeAddon(addonTypeKey, addon),
        cost,
        isDeliverable: false
      });
    });

    const deliverableCost = Math.max(totalCost - addonsTotal, 0);
    const credits = this.computeOutputCreditAmount(output);
    return [
      {
        type: output?.isMaster ? 'video' : 'iteration',
        desc: baseDesc,
        cost: deliverableCost,
        isDeliverable: true,
        credits
      },
      ...addonItems
    ];
  };

  // Thin wrapper around the pure helper in ../utils so the snapshot-build
  // logic is unit-testable without spinning up the component. The team
  // context lives on state, so it's resolved here and passed through.
  // The helper itself owns the ADR-0021 invoice-pathway status choice via
  // pickInvoiceStatusForOutput.
  buildBillablesFromLineItems = (lineItems, dollarsPortion, invoicePortion, output) =>
    buildBillablesFromLineItemsUtil({
      lineItems,
      dollarsPortion,
      invoicePortion,
      output,
      selectedTeamId: this.state.selectedTeamId,
      selectedTeamName: this.getSelectedTeamName()
    });

  // Single entry point for the finance-ledger flow. Handles both dollars
  // (greedy split when balance < totalCost) and the all-invoice case
  // (useShootsDollars=false → dollarsLeft starts at 0).
  handleSaveWithFinanceLedger = async ({ selectedReasons = [] } = {}) => {
    const {
      billFinanceWorkspace,
      getFinanceBalance,
      onConfirmShootsDollars,
      onCreditsUpdated,
      productOrigin = 'pro',
      video,
      videoOutputs,
      creditType
    } = this.props;
    const {
      purchaseOrderId,
      totalCost,
      dollarsRemaining,
      selectedTeamId,
      useShootsDollars
    } = this.state;

    if (!billFinanceWorkspace || !video?.videoId) {
      notify({ type: 'error', content: 'Finance ledger is not available right now' });
      return;
    }

    const orderedOutputs = [
      ...(videoOutputs || []).filter((o) => o?.isMaster),
      ...(videoOutputs || []).filter((o) => !o?.isMaster)
    ];

    if (orderedOutputs.length === 0) {
      notify({ type: 'error', content: 'No outputs to bill' });
      return;
    }

    // Team-mode orgs must attribute every credit billable to a team — the
    // backend rejects a credit bill with no teamAllocations ("teamAllocations
    // required in team mode"). selectedTeamId is only set once the team picker
    // has loaded (setCreditInfo). If it never loaded for a credit-funded
    // team-mode submit, stop here with a clear message rather than firing a
    // doomed bill that 400s with a generic error. Scoped strictly to team-mode
    // orgs (creditType === 'TEAM') that actually consume credits on this
    // submit — dollar/invoice-only submits never need a team.
    const isTeamModeOrg = creditType === 'TEAM';
    const consumesCredits = orderedOutputs.some(
      (o) => this.computeOutputCreditAmount(o) > 0
    );
    if (isTeamModeOrg && consumesCredits && !selectedTeamId) {
      notify({
        type: 'error',
        content:
          "We couldn't load your teams for this project, so it can't be submitted yet. Please close and reopen this window, then choose a team before submitting."
      });
      return;
    }

    const authUser = (credentials.get && credentials.get()) || {};
    const requestedBy =
      (authUser.user && (authUser.user.email || authUser.user.userId)) ||
      'unknown-user';
    const appUrl = globalConfig.get('appUrl') || window.location.origin;
    const trafficBase = appUrl.replace(/^(https?:\/\/)[^.]+\./, '$1shootsta.');

    const opening = useShootsDollars ? dollarsRemaining : 0;
    let dollarsLeft = opening;
    let dollarsSpent = 0;

    this.setState({ financeBusy: true });

    try {
      for (const output of orderedOutputs) {
        const { totalCost: outputCost } = getOutputSummaryWithAdditionalOutputs(
          output,
          [],
          this.props.addonsSelector,
          this.props.isBrief
        );
        const summaryCost = Number(outputCost) || 0;
        // Outputs whose price isn't expressed inside `appliedAddons` (most
        // notably Social Iterations, which carry their cost on the iteration
        // record as `outputCost: "AUD $300"`) come out of the summary as 0.
        // Without this fallback the iteration is skipped, billFinanceWorkspace
        // never fires, and the backend's submitMasterOutput then charges
        // dollars regardless of the user's "Bill to my organisation" choice.
        const cost = summaryCost > 0
          ? summaryCost
          : getCostValue(output, 'outputCost');
        const credits = this.computeOutputCreditAmount(output);
        // Skip outputs with neither dollar cost nor credit consumption —
        // nothing to bill. Iterations with cost === 0 (included-in-package)
        // and non-master outputs with no addons land here.
        if (cost <= 0 && credits <= 0) continue;

        const dollarsPortion = Math.min(cost, dollarsLeft);
        const invoicePortion = cost - dollarsPortion;
        dollarsLeft -= dollarsPortion;
        dollarsSpent += dollarsPortion;

        const lineItems = this.buildLineItemsForOutput(output, cost);
        const billables = this.buildBillablesFromLineItems(
          lineItems,
          dollarsPortion,
          invoicePortion,
          output
        );
        if (billables.length === 0) continue;

        const { data, error } = await billFinanceWorkspace({
          requestedBy,
          productOrigin,
          productRecordId: output.outputId,
          platformUrl: `${trafficBase}/traffic/video/${video.videoId}`,
          billables
        });

        if (error || !data?.billFinanceWorkspace) {
          notify({
            type: 'error',
            content: this.getBillFinanceErrorMessage(error)
          });
          return;
        }
      }

      let current = Math.max(opening - dollarsSpent, 0);
      if (getFinanceBalance) {
        try {
          const { data: balanceData } = await getFinanceBalance({});
          if (balanceData?.getFinanceBalance) {
            current = Number(balanceData.getFinanceBalance.dollarsRemaining) || 0;
            this.setState({ dollarsRemaining: current });
          }
        } catch (e) {
          // best-effort refresh; fall back to computed value
        }
      }

      const dollarsBalanceSnapshot = useShootsDollars
        ? {
            opening,
            spent: dollarsSpent,
            current,
            totalCost,
            invoicedShortfall: Math.max(totalCost - opening, 0)
          }
        : null;

      if (onConfirmShootsDollars) {
        await onConfirmShootsDollars({
          purchaseOrderId,
          clientRejectionReasons: selectedReasons,
          hasCost: false,
          ...(selectedTeamId ? { teamId: selectedTeamId } : {}),
          dollarsBalanceSnapshot
        });
      }

      onCreditsUpdated?.();
    } catch (err) {
      notify({
        type: 'error',
        content: this.getBillFinanceErrorMessage(err)
      });
      // eslint-disable-next-line no-console
      console.error('[Finance Ledger] submit failed:', err);
    } finally {
      this.setState({ financeBusy: false });
    }
  };

  modalSaveHandler = () => {
    const { showConfirmationModal, stayOpen } = this.state;

    if (stayOpen) {return this.goToConfirmationModal;}

    // eslint-disable-next-line consistent-return
    if (showConfirmationModal) {return void 0;}

    return this.handleSave;
  };

  setShutterStockAccess = async () => {
    const {
      getOrganisation,
      flags
    } = this.props;
    const { showShutterstockAssets = false } = flags || {};

    if (!showShutterstockAssets || !getOrganisation) {
      return;
    }

    const { data, error } = await getOrganisation();

    if (!data || error) {
      return;
    }

    this.setState({
      hasShutterStockAccess: _get(data, 'getOrganisation.shutterstockAccess', false)
    });
  };

  setCreditInfo = async () => {
    const {
      getTeamCredits,
      getOrgCredits,
      getContractBalance,
      flags,
      video: { organisationId },
      getUserTeams,
      organisationMemberships,
      isWorkspaceAdmin
    } = this.props;

    if (!getTeamCredits || !getOrgCredits || !getUserTeams) {
      return;
    }

    const { subscriptionsV22 = false } = flags || {};

    if (subscriptionsV22 && !getContractBalance) {
      return;
    }

    this.setState({ loading: true });

    try {
      const orgCreditResponse = await getOrgCredits();
      const { data: orgCreditData, error: orgCreditError } = orgCreditResponse;

      if (!orgCreditData || orgCreditError) {
        this.setState({ loading: false });
        return;
      }

      const creditType = _get(
        orgCreditData,
        'getOrganisation.package.creditType'
      );

      if (creditType === 'SHARE') {
        const { orgCredits } = await contractBalanceForSubscription(
          organisationId,
          getContractBalance,
          flags,
          creditType
        );

        this.setState({
          loading: false,
          orgCreditInfo: subscriptionsV22 ?
          orgCredits :
          orgCreditData.getOrganisation,
          teamCreditInfo: [],
          selectedTeamId: ''
        });
        return;
      }

      const [teamCreditResponse, userTeamsResponse] = await Promise.all([
      getTeamCredits(),
      getUserTeams()]
      );

      const { data: teamCreditData, error: teamCreditError } = teamCreditResponse;
      const { data: userTeamsData, error: userTeamsError } = userTeamsResponse;

      if (!teamCreditData || teamCreditError || userTeamsError) {
        this.setState({ loading: false });
        return;
      }

      const userTeams = _get(userTeamsData, 'getUserTeams', []);

      const { teamCredits, orgCredits } = await contractBalanceForSubscription(
        organisationId,
        getContractBalance,
        flags,
        creditType
      );

      const userTeamIds = !isEmpty(userTeams) ?
      userTeams.map(({ teamId }) => teamId) :
      [];

      const userTeamCredits = getUserTeamCreditsByRole({
        organisationMemberships,
        teamCredits,
        userTeamIds,
        isWorkspaceAdmin
      });

      const userTeamCreditsData = !isEmpty(teamCreditData.getTeamMemberships) ?
      teamCreditData.getTeamMemberships.filter(({ team }) => userTeamIds.includes(team.teamId)
      ) :
      [];

      const baseTeamCreditInfo = subscriptionsV22 ?
      userTeamCredits :
      userTeamCreditsData;

      // Finance ledger: team credit balances are system-of-record in the
      // Finance app; the legacy team.creditBalance is 0, which made the Review
      // Submission team dropdown show "0 Credits Available" for every team.
      // Source each team's available credits from financeBalance.teamBalances
      // (teams absent from the ledger → 0, never omitted).
      const { financeBalance } = this.props;
      const teamCreditInfo = applyFinanceTeamCredits({
        enabled: Boolean(flags?.financeLedgerEnabled),
        teamCreditInfo: baseTeamCreditInfo,
        teamBalances: financeBalance?.teamBalances
      });
      const teamDetails = teamCreditInfo.length > 0 ?
      teamCreditInfo.map(({ team }) => team.teamId) :
      [];

      this.setState({
        loading: false,
        orgCreditInfo: subscriptionsV22 ?
        orgCredits :
        orgCreditData.getOrganisation,
        teamCreditInfo,
        selectedTeamId: subscriptionsV22 ?
        getSelectedTeamIdForSubscriptionsV2({ creditType, teamDetails }) :
        teamDetails[0]
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch credit details:', error);
      this.setState({ loading: false });
    }
  };

  onSelectTeam = (teamInfo) => {
    this.setState({ selectedTeamId: teamInfo.value });
  };

  setTotalCost = async () => {
    const { videoOutputs, addonsSelector, isBrief, isNewDesign } = this.props;

    const { master, withoutMaster } = detachMaster(videoOutputs);

    const { totalCost, addonsList } = getOutputSummaryWithAdditionalOutputs(
      master || videoOutputs[0], withoutMaster, addonsSelector, isBrief
    );

    const hasZeroTotalCost = totalCost === 0;

    this.setState(({ stayOpen: prevStayOpen }) => ({
      totalCost,
      stayOpen: prevStayOpen && !hasZeroTotalCost,
      showConfirmationModal: prevStayOpen && hasZeroTotalCost
    }));

    const { getProjectOrganisation } = this.props;

    if (!getProjectOrganisation) {
      return;
    }

    const { data, error } = await getProjectOrganisation();

    if (!data || error) {
      return;
    }

    const projectClientType = _get(data, 'getOrganisation.clientType', false);

    this.setState({
      clientType: projectClientType,
      showProceedToPayment: isNewDesign && projectClientType === 'B2C' && !isEmpty(addonsList)
    });
  };

  onLiveChatModalClose = () => {
    this.setState({ showLiveChatModal: false });
  };

  onShowLiveChatModal = () => {
    this.setState({ showLiveChatModal: true });
  };

  makePayment = async () => {
    // const { customPaymentCheckout } = this.props;
    const { video, videoOutputs } = this.props;
    const masterOutput = videoOutputs.find(({ isMaster }) => isMaster);
    if (isEmpty(masterOutput)) {
      console.error('master output not found for this video');
      return;
    }
    const { outputId } = masterOutput || {};
    const { videoId } = video || {};
    if (isEmpty(videoId) || isEmpty(outputId)) {
      console.error('invalid video details');
    }

    // make API call to customPaymentCheckout
    const { customPaymentCheckout } = this.props;
    const { purchaseOrderId, totalCost, selectedTeamId } = this.state;
    const originUrl = window.location.href;
    const { data, error } = await customPaymentCheckout({
      videoId,
      outputId,
      originUrl,
      paymentType: 'PROJECT_SUBMISSION',
      projectMetadata: {
        purchaseOrderId,
        hasCost: Boolean(totalCost),
        teamId: selectedTeamId
      }
    });

    if (!data || error) return;

    const { customPaymentCheckout: checkoutData } = data || {};

    const { checkoutUrl } = checkoutData;
    if (isEmpty(checkoutUrl)) return;

    window.location.href = checkoutUrl;
  };

  handleOnClickTopupCredit = async () => {
    const { onClickTopupCredit, video } = this.props;
    const { organisation } = video || {};
    const { alias } = organisation || {};

    const enabledPaymentGateway = await getFlagStatus({
      orgAlias: alias,
      flagName: 'enabled-payment-gateway'
    });

    if (enabledPaymentGateway && typeof onClickTopupCredit === 'function') {
      onClickTopupCredit();
      return;
    }

    this.onShowLiveChatModal();
  };

  render() {
    const {
      visible,
      videoOutputs,
      video,
      video: {
        name,
        requestedExtras,
        organisationId
      },
      onClose,
      outputStatus,
      showCreditInfo = false,
      getCurrencySymbol,
      creditCost = 1,
      addonsSelector,
      isBrief,
      hasComments,
      isNewDesign,
      flags,
      isUnlimited,
      financeBalance
    } = this.props;

    const {
      loading,
      totalCost,
      showConfirmationModal,
      stayOpen,
      orgCreditInfo,
      teamCreditInfo,
      selectedTeamId,
      showLiveChatModal,
      hasShutterStockAccess,
      showProceedToPayment,
      clientType,
      useShootsDollars,
      dollarsRemaining,
      financeBusy
    } = this.state;

    if (totalCost === -1) {return null;}

    const { subscriptionsV22 = false } = flags || {};
    const { package: { creditType } = {} } = orgCreditInfo;
    const financeLedgerEnabled = !!flags?.financeLedgerEnabled;

    // The balance is owned by CreditsProvider (context). While the flag is on
    // and it hasn't arrived yet, treat the modal as loading so we never gate or
    // bill against an unknown balance.
    const financeBalanceLoading = financeLedgerEnabled && financeBalance == null;

    // isUnlimited and financeBalance both come from CreditsProvider so the
    // create-project gate and this submit gate read the same source of truth.
    const cannotSubmitProject = !resolveCanSubmit({
      financeLedgerEnabled,
      isUnlimited,
      creditType,
      selectedTeamId,
      financeBalance,
      creditCost,
      teamCreditInfo,
      orgCreditInfo,
      isNewSubscriptionModelEnabled: subscriptionsV22
    });

    const shouldChargeCreditForB2C = isNewDesign && clientType === 'B2C' && cannotSubmitProject;

    const shouldMakePayment = showProceedToPayment || shouldChargeCreditForB2C;

    const showShootsDollarsCard = financeLedgerEnabled && totalCost > 0;
    const hasInsufficientDollars =
      useShootsDollars && dollarsRemaining < totalCost;

    // Workspace credit gate — Shootsta Dollars only covers the addons dollar
    // amount, never the per-project credit consumption, so credits must still
    // be available regardless of payment method. The !shouldMakePayment carve-
    // out preserves the B2C Stripe ("Proceed to Payment") path.
    const insufficientCredits =
      showCreditInfo && !shouldMakePayment && cannotSubmitProject;

    const isSaveDisabled =
      insufficientCredits ||
      (showShootsDollarsCard && hasInsufficientDollars) ||
      (financeLedgerEnabled && financeBalanceLoading);

    const isInternalReview = outputStatus === OUTPUT_STATUS.INTERNAL_REVIEW;
    const showTeamsCreditInfo = subscriptionsV22 && showCreditInfo ?
    canShowTeamCreditsInfo({ creditType }) : showCreditInfo;

    const showBufferWarning = subscriptionsV22 && showCreditInfo && !isSaveDisabled ?
    showBufferCreditWarning({
      orgCreditInfo,
      teamCreditInfo,
      selectedTeamId,
      creditCost
    }) :
    false;

    const paymentMethodNode = showShootsDollarsCard ? (
      <div className="payments-invoice-modal__payment-method">
        <Text noMargin highlighted className="payments-invoice-modal__payment-method__label">
          Payment Method
        </Text>
        <button
          type="button"
          onClick={this.openChangePaymentMethodModal}
          disabled={financeBusy || financeBalanceLoading}
          aria-busy={financeBalanceLoading}
          className={cx(
            'payments-invoice-modal__payment-method-card',
            { 'payments-invoice-modal__payment-method-card--error': hasInsufficientDollars && !financeBalanceLoading }
          )}
        >
          {financeBalanceLoading ? (
            <span className="payments-invoice-modal__payment-method-skeleton" aria-label="Loading payment methods">
              <span className="payments-invoice-modal__payment-method-skeleton__bar payments-invoice-modal__payment-method-skeleton__bar--label" />
              <span className="payments-invoice-modal__payment-method-skeleton__bar payments-invoice-modal__payment-method-skeleton__bar--description" />
            </span>
          ) : (
            <>
              <span className="payments-invoice-modal__payment-method-card__text">
                <span className="payments-invoice-modal__payment-method-card__text__label">
                  {useShootsDollars ? 'Shootsta Dollars' : 'Bill to my organisation'}
                </span>
                <span className="payments-invoice-modal__payment-method-card__text__description">
                  {useShootsDollars ?
                    `Balance: ${getCurrencySymbol()}${toCurrencyDecimal(dollarsRemaining)}` :
                    'Outstanding amounts will be billed to your organisation at the end of the month.'}
                </span>
              </span>
              <span className="payments-invoice-modal__payment-method-card__edit" aria-hidden="true">
                <Pencil size={18} strokeWidth={2} />
              </span>
            </>
          )}
        </button>
        {hasInsufficientDollars && !financeBalanceLoading && (
          <div className="payments-invoice-modal__insufficient-warning">
            <AlertTriangle size={16} strokeWidth={2.25} />
            <Text noMargin>
              Insufficient Shootsta dollars. Contact your Account Manager for more details.
            </Text>
          </div>
        )}
      </div>
    ) : null;

    const footerChildrenComponent = () => {
      if (!isNewDesign) {
        return (
          <Input
            className="payments-invoice-modal__purchase-order"
            label="Enter any special billing instructions or PO here (Optional)"
            placeholder="e.g. Bill to acmecorp or bill against PO1234"
            onChange={this.setPurchaseOrderId} />);


      }

      if (clientType !== 'B2B') return null;

      return (
        <Text className="payments-invoice-modal__foot-note" caption>
          † As these costs may change during production,
          you will be invoiced after project completion.
          By clicking ‘Confirm & Submit’, you agree to our
          {' '}
          <Link onClick={openFairPlayPolicy} className="payments-invoice-modal__foot-note__link">
            Terms of Service.
          </Link>
        </Text>);

    };

    return (
      <div className={cx('payments-invoice-modal', { 'output-changes-modal': showConfirmationModal })}>
        {showConfirmationModal ?
        <ChangesModal
          visible={visible}
          onClose={onClose}
          saveText="Submit Changes"
          onSave={this.handleSave}
          stayOpenOnSave={stayOpen}
          columns={isInternalReview}
          multiple
          hasComments={hasComments}
          {...getChangesModalConstants(outputStatus)} /> :


        <>
            <FormModal
            visible={visible}
            onClose={onClose}
            saveText={shouldMakePayment ? 'Proceed to Payment' : 'Confirm & Submit'}
            onSave={shouldMakePayment ? this.makePayment : this.modalSaveHandler()}
            stayOpenOnSave={stayOpen}
            saveDisabled={isSaveDisabled || financeBusy}
            loadingSave={financeLedgerEnabled && (financeBalanceLoading || financeBusy)}
            title={isNewDesign ? 'Review Submission' : 'Confirm Project Submission'}
            subtitle={isNewDesign && 'Confirm and submit your project requirements to the editors. Commenting will be paused until your project is sent back to you for review.'}
            className={cx(
              'payments-invoice-modal__form',
              { 'payments-invoice-modal__modal-width': isNewDesign }
            )}
            footerChildren={footerChildrenComponent()}>

              <AddonsPayment
              video={video}
              isNewDesign={isNewDesign}
              setPurchaseOrderId={this.setPurchaseOrderId}
              videoOutputs={videoOutputs}
              addonsSelector={addonsSelector}
              organisationId={organisationId}
              requestedExtras={requestedExtras}
              videoName={name}
              totalCost={totalCost}
              showCreditInfo={showTeamsCreditInfo}
              orgCreditInfo={orgCreditInfo}
              teamCreditInfo={teamCreditInfo}
              getCurrencySymbol={getCurrencySymbol}
              creditCost={creditCost}
              isSaveDisabled={isSaveDisabled}
              hasShutterStockAccess={hasShutterStockAccess}
              onSelectTeam={this.onSelectTeam}
              isBrief={isBrief}
              onClickTopupCredit={this.handleOnClickTopupCredit}
              showBufferWarning={showBufferWarning}
              loading={loading}
              shouldChargeCreditForB2C={shouldChargeCreditForB2C}
              paymentMethodNode={paymentMethodNode} />

            </FormModal>
            {showShootsDollarsCard && (
              <ChangePaymentMethodModal
                visible={this.state.showChangePaymentMethodModal}
                onClose={this.closeChangePaymentMethodModal}
                onConfirm={this.handleChangePaymentMethod}
                selectedMethod={useShootsDollars ? 'dollars' : 'invoice'}
                dollarsRemaining={dollarsRemaining}
                currencySymbol={getCurrencySymbol()} />
            )}
            {showLiveChatModal &&
          <LiveChatModal
            saveButtonId="topup-credits-v1"
            visible={showLiveChatModal}
            closeModal={this.onLiveChatModalClose}
            saveText="Live Chat Now"
            title="Run out of credits?"
            contentHeading="We can top up your workspace or team credits instantly!"
            content={
            <>
                    <p>
                      Simply reach out to us on Live Chat, answer a few questions
                      & we can get this rolling straight away.
                    </p>
                    <p>Credits will be applied instantly & invoiced later</p>
                  </>} />}



          </>}

      </div>);

  }
}
export default withFlagsConsumer(PaymentsInvoice);